import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, CardHeader, Field, Input } from '@truecare/ui';
import { messageOf, unwrap } from '@truecare/api-client';
import { api } from '../api';
import { MARKETING_URL } from '../marketing-url';

/**
 * Doc 14 (SMS-e) — portal SMS-notification opt-in card.
 *
 * Off-by-default (CASL): a user with no consent sees a value-prop + an enable
 * form. Enabling records consent as "pending" and texts a "reply YES to confirm"
 * message (double opt-in); the YES lands on the backend inbound webhook. Shown
 * only for the SMS-capable personas (caregiver, homecare) — facility is email-only
 * — and only while SMS is switched on for the environment (`smsEnabled`).
 *
 * The opt-in form MUST match the mockup Twilio approved for toll-free
 * verification (docs/design/14a §3 — "this mockup is the contract"): unchecked
 * consent checkbox, transactional-only message types, frequency, rates,
 * HELP/STOP, Terms + Privacy links, and the double-opt-in note before submit.
 * Change the consent wording only together with SMS_CONSENT_COPY_VERSION
 * (backend sms-consent.service.ts) — acceptances are recorded against it.
 */

type Persona = 'CAREGIVER' | 'FACILITY_USER' | 'HOMECARE_CLIENT_USER' | 'OPERATOR';
type SmsPath = '/api/v1/me/caregiver/sms' | '/api/v1/me/homecare/sms';

interface SmsPref {
  state: 'none' | 'requested' | 'confirmed' | 'withdrawn';
  consentedPhoneLast4: string | null;
  smsEnabled: boolean;
}

function smsPathForRoles(roles: { persona: Persona }[] | undefined): SmsPath | null {
  if (!roles) return null;
  if (roles.some((r) => r.persona === 'CAREGIVER')) return '/api/v1/me/caregiver/sms';
  if (roles.some((r) => r.persona === 'HOMECARE_CLIENT_USER')) return '/api/v1/me/homecare/sms';
  return null; // facility / operator — email-only
}

export function SmsNotificationsCard() {
  const qc = useQueryClient();
  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => unwrap(api.GET('/api/v1/auth/me')),
  });
  const roles = (meQuery.data as { roles?: { persona: Persona }[] } | undefined)?.roles;
  const smsPath = smsPathForRoles(roles);
  const isCaregiver = smsPath === '/api/v1/me/caregiver/sms';

  const prefQuery = useQuery({
    queryKey: ['sms-pref', smsPath],
    enabled: smsPath !== null,
    queryFn: () => unwrap(api.GET(smsPath as SmsPath)),
  });

  const [phone, setPhone] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [editing, setEditing] = useState(false);
  const [notice, setNotice] = useState<{ text: string; tone: 'info' | 'warning' } | null>(null);

  const enableMut = useMutation({
    mutationFn: (p: string) =>
      unwrap(api.PUT(smsPath as SmsPath, { body: { phone: p } })),
    onSuccess: (res) => {
      const r = res as { confirmationSent?: boolean };
      setNotice(
        r.confirmationSent
          ? { tone: 'info', text: 'We texted you a confirmation — reply YES to turn on text alerts.' }
          : {
              tone: 'warning',
              text: 'Text alerts aren’t available yet — we couldn’t send a confirmation, so nothing was turned on. Please try again later.',
            },
      );
      setEditing(false);
      setPhone('');
      setAgreed(false);
      void qc.invalidateQueries({ queryKey: ['sms-pref', smsPath] });
    },
  });

  const disableMut = useMutation({
    mutationFn: () => unwrap(api.DELETE(smsPath as SmsPath)),
    onSuccess: () => {
      setNotice({ tone: 'info', text: 'Text alerts turned off.' });
      void qc.invalidateQueries({ queryKey: ['sms-pref', smsPath] });
    },
  });

  // Not an SMS-capable persona → render nothing.
  if (meQuery.isSuccess && smsPath === null) return null;
  if (!smsPath) return null;

  // Loading guard (audit 2026-10-20 MED): don't flash the opt-in FORM for an
  // already-confirmed user while the preference query is still in flight.
  if (prefQuery.isPending) {
    return (
      <Card padding="lg" accent="left" tone="forest">
        <CardHeader title="Text (SMS) notifications" />
        <p className="mt-4 font-body text-sm text-ink-muted">Loading…</p>
      </Card>
    );
  }

  const pref = prefQuery.data as SmsPref | undefined;
  // SMS switched off for this environment (SMS_ENABLED) → no card at all, so a
  // user is never offered a sign-up that can't send. The code ships dark to
  // production this way until SMS launches there.
  if (pref && !pref.smsEnabled) return null;
  const state = pref?.state ?? 'none';
  const last4 = pref?.consentedPhoneLast4 ?? null;
  const isOn = state === 'confirmed';
  const isPending = state === 'requested';
  const showForm = editing || state === 'none' || state === 'withdrawn';

  const valueProp = `${
    isCaregiver
      ? 'Get shift offers and schedule updates by text, so you don’t miss work.'
      : 'Get booking and visit updates by text, so you’re always in the loop.'
  } Off by default — turn it on below.`;

  return (
    <Card padding="lg" accent="left" tone="forest">
      <CardHeader
        title="Text (SMS) notifications"
        description={valueProp}
      />

      <div className="mt-4 flex flex-col gap-4">
        {notice ? (
          <div className="flex flex-col gap-1">
            <Alert tone={notice.tone} data-testid="sms-notice">
              {notice.text}
            </Alert>
            <button
              type="button"
              onClick={() => setNotice(null)}
              className="self-end font-body text-xs text-ink-muted underline decoration-dotted underline-offset-2 transition-colors hover:text-forest"
            >
              Dismiss
            </button>
          </div>
        ) : null}

        {isOn && !editing ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Alert tone="success" className="grow">
              Text alerts are on{last4 ? ` for the number ending ••${last4}` : ''}.
            </Alert>
          </div>
        ) : null}

        {isPending && !editing ? (
          <Alert tone="warning">
            Almost there — reply <strong>YES</strong> to the text we sent
            {last4 ? ` to the number ending ••${last4}` : ''} to turn alerts on. Reply{' '}
            <strong>STOP</strong> any time to opt out.
          </Alert>
        ) : null}

        {showForm ? (
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (phone.trim() && agreed) enableMut.mutate(phone.trim());
            }}
          >
            <Field
              label="Mobile number"
              id="sms-phone"
              hint="We’ll send text alerts to this number. Changing it later re-confirms your consent."
            >
              <Input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="(519) 555-0123"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </Field>
            <label className="flex items-start gap-3 rounded-md border border-border bg-cream px-4 py-3 font-body text-sm">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1"
                data-testid="sms-consent-checkbox"
              />
              <span className="flex flex-col gap-1">
                <strong className="text-ink">Yes — text me shift &amp; booking alerts.</strong>
                <span className="text-ink-muted">
                  You’ll receive <strong>transactional messages only</strong> — shift
                  offers, shift reminders, schedule changes, and booking or payment
                  updates. No marketing. Message frequency varies. Message and data rates
                  may apply. Reply <strong>HELP</strong> for help, <strong>STOP</strong> to
                  cancel anytime. By checking this box you agree to receive these texts
                  and to our{' '}
                  <a
                    href={`${MARKETING_URL}/sms-terms`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-forest underline underline-offset-2"
                  >
                    SMS Terms of Service
                  </a>{' '}
                  and{' '}
                  <a
                    href={`${MARKETING_URL}/privacy`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-forest underline underline-offset-2"
                  >
                    Privacy Policy
                  </a>
                  .
                </span>
              </span>
            </label>
            <Alert tone="info" data-testid="sms-double-opt-in-note">
              After you save, we’ll text this number to confirm. Reply <strong>YES</strong>{' '}
              to start receiving alerts.
            </Alert>
            {enableMut.isError ? (
              <Alert tone="danger">{messageOf(enableMut.error)}</Alert>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Button
                type="submit"
                disabled={!phone.trim() || !agreed || enableMut.isPending}
              >
                {enableMut.isPending ? 'Sending…' : 'Yes, sign me up'}
              </Button>
              {editing ? (
                <Button type="button" variant="secondary" onClick={() => setEditing(false)}>
                  Cancel
                </Button>
              ) : null}
            </div>
          </form>
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => disableMut.mutate()}
              disabled={disableMut.isPending}
            >
              {disableMut.isPending ? 'Turning off…' : 'Turn off'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setEditing(true)}>
              Use a different number
            </Button>
          </div>
        )}
        {disableMut.isError ? (
          <Alert tone="danger">{messageOf(disableMut.error)}</Alert>
        ) : null}
      </div>
    </Card>
  );
}
